import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Returns a referentially stable function that always dispatches to the latest `callback`.
 *
 * Use it where a handler must be handed to a non-React consumer during render (a form-config
 * factory, an imperative API) but its implementation closes over refs or fresh state: the
 * consumer holds one stable function for the component's lifetime, and each call reaches the
 * implementation from the most recent commit. The implementation itself is untouched, so any
 * synchronous guard inside it (e.g. a double-submit ref check) behaves exactly as before.
 *
 * The latest implementation is installed in a layout effect (synchronously during commit), so a
 * call from any post-commit source (user event, Stripe iframe callback, timer) never sees a stale
 * implementation.
 */
export function useLatestCallback<TArgs extends unknown[], TResult>(callback: (...args: TArgs) => TResult): (...args: TArgs) => TResult {
    const callbackRef = useRef(callback);
    useLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);
    return useCallback((...args: TArgs) => callbackRef.current(...args), []);
}
